import { Button } from "antd";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { useEffect, useState } from "react";

import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { scheduleService } from "@/api/services/scheduleService";
import type { OrderVo, ScheduleDateVo, ScheduleVo } from "@zhihaoscm/entity";
import { dateFormat, fmtNum } from "@zhihaoscm/utils";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

interface SchedulePageState {
    page: number;
    size: number;
    total: number;
}

interface SchedulePageResult {
    list?: ScheduleVo[];
    total?: number;
}

const DEFAULT_PAGE_SIZE = 5;
const scheduleStateLabels: Record<number, string> = {
    1: "待确认",
    2: "已确认",
    3: "已提货",
    4: "已取消",
    6: "已取消"
};

/** 旧 Vue 的排期日历按订单创建日与提货开始日的较晚日期作为查询起点。 */
export function getTradeOrderScheduleQuery(order: OrderVo["order"]) {
    const createdDay = order.createdTime ? dateFormat(order.createdTime) : "";
    const deliveryStartDay = order.deliveryStartTime ? dateFormat(order.deliveryStartTime) : "";
    const beginDay =
        createdDay && deliveryStartDay && new Date(createdDay).getTime() >= new Date(deliveryStartDay).getTime()
            ? createdDay
            : deliveryStartDay;

    if (!order.id || !beginDay || !order.deliveryEndTime) return null;
    return {
        orderId: String(order.id),
        beginTime: dateFormat(beginDay, LOG_DATE_FORMAT_START),
        endTime: dateFormat(order.deliveryEndTime, LOG_DATE_FORMAT_END)
    };
}

function formatTon(value: number | string | undefined): string {
    return fmtNum(value);
}

function formatScheduleAddress(row: ScheduleVo): string {
    const address = row.schedule?.addressInfo;
    if (!address) return "--";
    return [address.province, address.city, address.district, address.detailAddress].filter(Boolean).join("") || "--";
}

function formatShipName(row: ScheduleVo): string {
    return row.schedule?.shipInfo?.cnname || row.schedule?.shipInfo?.name || "--";
}

function scheduleColumns(): TableColumnProps<ScheduleVo>[] {
    return [
        { key: "id", label: "排期编号", prop: "schedule.id", render: ({ row }) => row.schedule?.id || "--" },
        {
            key: "deliveryId",
            label: "关联提货",
            prop: "delivery.id",
            render: ({ row }) => row.delivery?.id || "--"
        },
        {
            key: "scheduleDate",
            label: "排期日期",
            prop: "schedule.scheduleDate",
            render: ({ row }) => dateFormat(row.schedule?.scheduleDate) || "--"
        },
        {
            key: "estimatedTon",
            label: "预估提货吨数（吨）",
            prop: "schedule.estimatedTon",
            render: ({ row }) => formatTon(row.schedule?.estimatedTon)
        },
        { key: "shipName", label: "船舶", prop: "shipName", render: ({ row }) => formatShipName(row) },
        {
            key: "transportOrderShipId",
            label: "关联运单",
            prop: "transportOrderShipId",
            render: ({ row }) =>
                row.transportOrderShip?.id
                    ? `${row.transportOrderShip.id}（${scheduleStateLabels[Number(row.transportOrderShip.state)] || "--"}）`
                    : "--"
        },
        { key: "address", label: "收货信息", prop: "address", render: ({ row }) => formatScheduleAddress(row) },
        {
            key: "state",
            label: "排期状态",
            prop: "schedule.state",
            render: ({ row }) => scheduleStateLabels[Number(row.schedule?.state)] || "--"
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            width: 120,
            render: () => (
                <>
                    <Button type="link">修改</Button>
                    <Button type="link">删除</Button>
                </>
            )
        }
    ];
}

export interface TradeOrderSchedulePanelProps {
    detail: OrderVo;
}

/** 交易订单排期信息，保留旧 Vue 的指标、日期查询和五条分页规则。 */
export function TradeOrderSchedulePanel({ detail }: TradeOrderSchedulePanelProps) {
    const [scheduleDates, setScheduleDates] = useState<ScheduleDateVo[]>([]);
    const [scheduleRows, setScheduleRows] = useState<ScheduleVo[]>([]);
    const [calendarPage, setCalendarPage] = useState(0);
    const [pageState, setPageState] = useState<SchedulePageState>({
        page: 1,
        size: DEFAULT_PAGE_SIZE,
        total: 0
    });
    const order = detail.order;
    const query = getTradeOrderScheduleQuery(order);

    useEffect(() => {
        let disposed = false;
        setScheduleDates([]);
        setScheduleRows([]);
        setCalendarPage(0);
        setPageState({ page: 1, size: DEFAULT_PAGE_SIZE, total: 0 });
        if (!query) return;

        // 旧 Vue 分别请求日历分组与排期明细分页，保留各自 API payload。
        void Promise.all([
            scheduleService.scheduleDate(query),
            scheduleService.schedulePaging({
                page: 1,
                size: DEFAULT_PAGE_SIZE,
                orderId: query.orderId
            }) as unknown as Promise<SchedulePageResult>
        ])
            .then(([dateResult, pageResult]) => {
                if (disposed) return;
                setScheduleDates(dateResult || []);
                setScheduleRows(pageResult?.list || []);
                setPageState({ page: 1, size: DEFAULT_PAGE_SIZE, total: pageResult?.total || 0 });
            })
            .catch(() => {
                if (disposed) return;
                setScheduleDates([]);
                setScheduleRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [query?.beginTime, query?.endTime, query?.orderId]);

    const handlePageChange = (page: number, pageSize: number) => {
        if (!query) return;
        const nextPage = pageSize !== pageState.size ? 1 : page;
        setPageState(current => ({ ...current, page: nextPage, size: pageSize }));
        const schedulePromise = scheduleService.schedulePaging({
            page: nextPage,
            size: pageSize,
            orderId: query.orderId
        }) as unknown as Promise<SchedulePageResult>;
        void schedulePromise
            .then(result => {
                setScheduleRows(result?.list || []);
                setPageState(current => ({
                    ...current,
                    page: nextPage,
                    size: pageSize,
                    total: result?.total || current.total
                }));
            })
            .catch(() => setScheduleRows([]));
    };

    const remainingLabel = Number(order.orderType) === 3 ? "剩余提货吨数（吨）" : "预估剩余提货吨数（吨）";
    const remainingTon =
        detail.remainPickedUpTon === undefined || detail.remainPickedUpTon === null
            ? "--"
            : Number(detail.remainPickedUpTon) <= 0
              ? "0"
              : formatTon(detail.remainPickedUpTon);
    const calendarPageSize = 15;
    const calendarPageCount = Math.max(1, Math.ceil(scheduleDates.length / calendarPageSize));
    const visibleScheduleDates = scheduleDates.slice(
        calendarPage * calendarPageSize,
        (calendarPage + 1) * calendarPageSize
    );

    // 日历格展示旧 Vue 的“可提货/不可提货”状态；排期日期接口返回的分组数据是唯一数据源。
    const renderScheduleDate = (item: ScheduleDateVo) => {
        const dateValue = new Date(item.scheduleDate);
        const weekday = dateValue.toLocaleDateString("zh-CN", { weekday: "short" });
        const hasSchedule = Boolean(item.scheduleVos?.length);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isFuture = dateValue.getTime() >= today.getTime();
        return (
            <div className="trade-order-schedule-calendar__cell" key={item.scheduleDate}>
                <div className="trade-order-schedule-calendar__date">
                    <div className="trade-order-schedule-calendar__date-left">
                        <span>{dateFormat(item.scheduleDate)}</span>
                        <span className="trade-order-schedule-calendar__weekday">{weekday}</span>
                    </div>
                </div>
                <div
                    className={`trade-order-schedule-calendar__status ${hasSchedule ? "is-available" : "is-unavailable"}`}
                >
                    <span className="trade-order-schedule-calendar__dot">●</span>
                    {hasSchedule ? "可提货" : isFuture ? "预估可提货0 吨" : "不可提货"}
                </div>
                {hasSchedule ? (
                    <div className="trade-order-schedule-calendar__ton">
                        {formatTon(
                            item.scheduledTon ||
                                item.scheduleVos.reduce((sum, row) => sum + Number(row.schedule?.estimatedTon || 0), 0)
                        )}{" "}
                        吨
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <section className="section-card mt-4 trade-order-schedule-panel" data-testid="trade-order-schedule-panel">
            <div className="trade-order-schedule-panel__header">
                <div className="section-title mb-0">排期提货信息</div>
                <div className="trade-order-schedule-panel__month-actions">
                    <Button
                        type="primary"
                        ghost
                        size="middle"
                        disabled={calendarPage <= 0}
                        onClick={() => setCalendarPage(page => Math.max(0, page - 1))}
                    >
                        <i aria-hidden="true" className="iconfont icon-arrow-left" />
                        上一页
                    </Button>
                    <Button
                        type="primary"
                        ghost
                        size="middle"
                        disabled={calendarPage >= calendarPageCount - 1}
                        onClick={() => setCalendarPage(page => Math.min(calendarPageCount - 1, page + 1))}
                    >
                        下一页
                        <i aria-hidden="true" className="iconfont icon-arrow-rifht" />
                    </Button>
                </div>
            </div>
            <div className="trade-order-schedule-metrics">
                <div>
                    <span>可提货吨数（吨）</span>
                    <strong>{formatTon(order.ton)}</strong>
                </div>
                <div>
                    <span>排期提货吨数（吨）</span>
                    <strong>{formatTon(detail.scheduledDeliveryTon)}</strong>
                </div>
                <div>
                    <span>已提货吨数（吨）</span>
                    <strong>{formatTon(detail.pickedUpTon)}</strong>
                </div>
                <div>
                    <span>{remainingLabel}</span>
                    <strong>{remainingTon}</strong>
                </div>
            </div>
            {scheduleDates.length ? (
                <div className="trade-order-schedule-calendar" data-testid="trade-order-schedule-dates">
                    {visibleScheduleDates.map(renderScheduleDate)}
                </div>
            ) : null}
            <div className="trade-order-schedule-list">
                <ZhTable<ScheduleVo>
                    data={scheduleRows}
                    columns={scheduleColumns()}
                    pagination={false}
                    rowKey={row => String(row.schedule?.id)}
                    append={
                        pageState.total > 5 ? (
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={pageState.page}
                                    pageSize={pageState.size}
                                    total={pageState.total}
                                    pageSizeOptions={[5, 10, 20, 30, 40, 50, 100]}
                                    onChange={handlePageChange}
                                />
                            </div>
                        ) : null
                    }
                />
            </div>
        </section>
    );
}

export default TradeOrderSchedulePanel;
